import { BrandProfileField, BrandProfileFieldName, BrandProfilePayload } from 'domains/brand/types';

export type BrandLearningStrengthStatusCopy = { status: string; nextStep: string | null };

/** Order for brand profile fields in edit form, strength checklist, and next-step hints. */
export const BRAND_PROFILE_FIELD_DISPLAY_ORDER: BrandProfileFieldName[] = [
    BrandProfileFieldName.OrganizationName,
    BrandProfileFieldName.BrandDescription,
    BrandProfileFieldName.BrandToneKeywords,
    BrandProfileFieldName.SampleTemplateIds,
    BrandProfileFieldName.UseKeywords,
    BrandProfileFieldName.DontUseKeywords,
];

/**
 * Status + next-step copy keyed by the highest-priority unfilled field.
 * Mandatory fields (sorted by weightage desc) take precedence over optional fields.
 * Within optional fields the same precedence rule applies.
 */
const STATUS_COPY_BY_HIGHEST_UNFILLED: Partial<Record<BrandProfileFieldName, BrandLearningStrengthStatusCopy>> = {
    [BrandProfileFieldName.OrganizationName]: {
        status:
            "The more information you add, the better the AI can generate messages that match your brand. Let's start with your organization's name.",
        nextStep: null,
    },
    [BrandProfileFieldName.BrandDescription]: {
        status: 'Nice! AI can now reference your organization correctly.',
        nextStep: 'Add a brand description to teach AI how you communicate.',
    },
    [BrandProfileFieldName.BrandToneKeywords]: {
        status: 'Great! This helps AI understand your communication style.',
        nextStep: "Add tone keywords so AI can match your brand's voice.",
    },
    [BrandProfileFieldName.SampleTemplateIds]: {
        status: 'Perfect! AI now has a clearer sense of your tone.',
        nextStep: 'Add sample writing so AI can learn from real examples.',
    },
    [BrandProfileFieldName.UseKeywords]: {
        status: 'Perfect! AI now has a clearer sense of your tone.',
        nextStep: 'Add words to use so AI can learn your preferred phrases.',
    },
    [BrandProfileFieldName.DontUseKeywords]: {
        status: 'Perfect! AI now has a clearer sense of your tone.',
        nextStep: 'Add words to avoid so AI can match your brand voice.',
    },
};

export const CHECKLIST_LABELS: Record<BrandProfileFieldName, string> = {
    [BrandProfileFieldName.OrganizationName]: 'Organization name',
    [BrandProfileFieldName.BrandDescription]: 'Brand description',
    [BrandProfileFieldName.BrandToneKeywords]: 'Tone keywords',
    [BrandProfileFieldName.UseKeywords]: 'Words to use',
    [BrandProfileFieldName.DontUseKeywords]: 'Words to avoid',
    [BrandProfileFieldName.SampleTemplateIds]: 'Sample content',
};

export const isFieldSatisfied = (
    value: string | string[] | number[] | null | undefined,
    fieldName?: string
): boolean => {
    if (value === null || value === undefined) return false;
    if (value === '') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    if (fieldName === BrandProfileFieldName.BrandDescription && typeof value === 'string') {
        const wordCount = value.trim() ? value.trim().split(/\s+/).filter(Boolean).length : 0;
        return wordCount >= 50;
    }
    return true;
};

export const getFieldValue = (
    field: BrandProfileField,
    formValues?: BrandProfilePayload
): string | string[] | number[] | null | undefined => {
    if (formValues && field.fieldName in formValues) {
        return formValues[field.fieldName];
    }
    return field.currentValue;
};

export const calculatePercentage = (brandProfile: BrandProfileField[], formValues?: BrandProfilePayload): number => {
    const totalWeight = brandProfile.reduce((sum, f) => sum + f.weightage, 0);
    let satisfiedWeight = 0;
    brandProfile.forEach((f) => {
        const value = getFieldValue(f, formValues);
        if (isFieldSatisfied(value, f.fieldName)) satisfiedWeight += f.weightage;
    });
    return totalWeight > 0 ? Math.round((satisfiedWeight / totalWeight) * 100) : 0;
};

export const getStatusCopy = (
    brandProfile: BrandProfileField[],
    formValues?: BrandProfilePayload
): BrandLearningStrengthStatusCopy => {
    // Sort: mandatory fields first, then optional; highest weightage first within each tier.
    const sorted = [...brandProfile].sort((a, b) => {
        if (a.isRequired !== b.isRequired) return a.isRequired ? -1 : 1;
        return b.weightage - a.weightage;
    });

    const highestUnfilledField = sorted.find(
        (field) => !isFieldSatisfied(getFieldValue(field, formValues), field.fieldName)
    );

    if (!highestUnfilledField) {
        return {
            status:
                "Great! AI will avoid these in future messages. You're all set! Save to use your brand voice in future messages.",
            nextStep: null,
        };
    }

    return (
        STATUS_COPY_BY_HIGHEST_UNFILLED[highestUnfilledField.fieldName as BrandProfileFieldName] ?? {
            status:
                "The more information you add, the better the AI can generate messages that match your brand. Let's start with your organization's name.",
            nextStep: null,
        }
    );
};
